import React from 'react';

import { setLocale as setYupLocale } from 'yup';
import Polyglot from 'node-polyglot';
import { languages } from './constants';
import dayjs from 'dayjs';
import version from 'src/config/version.json';

let currentLanguageCode = '';
let antdLocale: any = null;

const polyglot = new Polyglot();
export const DEFAULT_LANGUAGE = 'en';

async function fetchTranslations(languageCode: string) {
  const response = await fetch(
    `/translations/${languageCode}.json?vs=${version.version}`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    },
  );

  return response.json();
}

/**
 * Loads everything the UI needs for one language in parallel:
 * the translation file plus the antd and dayjs locale modules of
 * that language only (the other 25 languages are never fetched).
 */
export async function init(lang?: string) {
  const browserLang = window.navigator.language.split('-')[0];

  const savedLanguage = lang || localStorage.getItem('language') || browserLang;
  currentLanguageCode =
    savedLanguage && languages[savedLanguage]
      ? savedLanguage
      : DEFAULT_LANGUAGE;
  setLanguageCode(currentLanguageCode);

  // Screen readers pick pronunciation from this attribute. Without it
  // every one of the 26 languages is announced as English (WCAG 3.1.1).
  if (typeof document !== 'undefined') {
    document.documentElement.lang = currentLanguageCode;
  }

  const language = getLanguage();

  // A locale chunk that fails to load must not take the
  // translations down with it: the UI is usable without antd
  // date-picker labels, it is not usable with raw keys.
  const loadOptional = (load: () => Promise<any>) =>
    load().catch(error => {
      console.error(error);
      return null;
    });

  try {
    const [data, antdModule] = await Promise.all([
      fetchTranslations(currentLanguageCode),
      loadOptional(language.antd),
      loadOptional(language.date),
    ]);

    antdLocale = antdModule ? (antdModule.default ?? antdModule) : null;

    polyglot.locale(currentLanguageCode);
    polyglot.extend(data);

    // The dayjs locale module registered itself when imported
    dayjs.locale(antdLocale?.locale ?? currentLanguageCode);

    if (data.validation) {
      setYupLocale(data.validation);
    }
  } catch (err) {
    console.error(err);
  }
}

function getLanguage() {
  return languages[getLanguageCode()];
}

export function getLanguages() {
  return Object.values(languages);
}

/**
 * The resolved antd locale object for `ConfigProvider`
 * (available after `init()`).
 */
export function getAntdLanguage() {
  return antdLocale;
}

export function getLanguageCode() {
  return currentLanguageCode;
}

export function setLanguageCode(arg) {
  const locale = languages[arg] ? arg : DEFAULT_LANGUAGE;

  localStorage.setItem('language', locale);
}

export function i18n(key, ...args) {
  return polyglot.t(key, ...args);
}

export function i18nHtml(key, ...args) {
  return (
    <span
      dangerouslySetInnerHTML={{
        __html: i18n(key, ...args),
      }}
    />
  );
}
